import {EGramLevelType, SkMBase, SkMCompo, SKMETALIB, SkMPart} from "lib/edit/schema/schemaMeta";
import {SchemaDom, SkContext, SkNode} from "lib/edit/schema/schema";
import {ISkRuleObj, SkRuleAttr, SkRuleNode} from "lib/edit/schema/schemaPatterns";
import {SK_NS} from "lib/edit/schema/schemaBuilder";
import {replaceXmlContent, XmlAddrMsg, XmlInsertMsg} from "lib/edit/ot/xmlHouse";
import {DOM} from "lib/commons/xml/dom";
import {SkAnnotEltFree} from "lib/edit/schema/schemaAnnots";
import {IXAddr, XA} from "lib/commons/xml/xAddr";
import {IMsgUpdater, MsgOt} from "lib/edit/ot/houseOt";
import {House, State} from "lib/edit/ot/urban";
import {XmlTypedState} from "lib/edit/ot/xmlTypedHouse";
import IS_element = DOM.IS_element;

/** SkMeta d'un élément modélisant une composition impliquée dans uen hiérarchie SpTe. */
export class SkMSpTeCompo extends SkMCompo {

	onExecRules(skCtx: SkContext, rule: SkRuleNode & ISpTeSpatialCoordsProps, node: Element): void {
		const execOpt = skCtx.execOptions;
		if (!execOpt.resetAll && (execOpt.autoComplete || execOpt.genAnnots)) planContinuousCheck(skCtx, node);
	}
}

SKMETALIB.registerMetaNode(new SkMSpTeCompo('SpTeCompo'));

type ISpTeShape = 'rect' | 'circle' | 'ellipse' | 'poly';

/** SkMeta d'un élément modélisant une racine SpTe. */
export class SkMSpTeCompoRoot extends SkMSpTeCompo {

	initSkRule(skRule: SkRuleNode & ISpTeCompoRootsProps, confRule: Element): void {
		const v = confRule.getAttributeNS(SK_NS, 'temporal'); //FIXME Schema sk:continuous
		skRule.continuousTracks = v != null ? v.split(' ') : [];
	}

	/** Tracks temporelles continues gérées par ce Root. */
	getContinuousTracks(skRule: ISkRuleObj & ISpTeCompoRootsProps): string[] {
		return skRule.continuousTracks;
	}

	onExecRules(skCtx: SkContext, rule: ISkRuleObj, node: Element): void {
		const execOpt = skCtx.execOptions;
		//Si ni genAnnot ni autoComplete, on sort.
		if (!execOpt.genAnnots && !execOpt.autoComplete) return;
		//Si d'autres corrections déjà en cours, on validera au prochain cycle.
		if (execOpt.corrections && execOpt.corrections.length > 0) return;
		//Si une revalid par un descendant est déjà planifiée, on attend cette revalid.
		if (execOpt.shouldRevalid && execOpt.shouldRevalid.has(node)) return;
		for (const track of this.getContinuousTracks(rule)) {
			new SpTeRootContinuousTrack(track).onExecRules(skCtx, rule, node);
		}
	}

	/**
	 *
	 */
	getAspect<A>(keyAspect: string, rule: SkRuleNode | SkRuleAttr, skNode: SkNode & ISpTeRootSkNode, options: any): A {
		if (keyAspect === "ContinuousSegMgr" && skNode.rule === rule) {
			if (!skNode.continuousSegMgr) skNode.continuousSegMgr = new ContinuousSegMgr(skNode, options.track); //FIXME Map de ContinuousSegMgr par track ?
			return skNode.continuousSegMgr as any;
		}
		return null;
	}
}

interface ISpTeRootSkNode {
	/** ref du singleton ContinuousSegMgr associé au SkNode. */
	continuousSegMgr?: ContinuousSegMgr
}

interface ISpTeCompoRootsProps {
	/** tracks temporelles continues gérées par ce root. */
	continuousTracks?: string[]
}

SKMETALIB.registerMetaNode(new SkMSpTeCompoRoot('SpTeCompoRoot'));


/** SkMeta d'un élément modélisant une segment d'un SpTe qui peut être spatial et/ou temporel. */
export class SkMSpTeSegment extends SkMPart {

	onExecRules(skCtx: SkContext, rule: SkRuleNode & ISpTeSpatialCoordsProps, node: Element): void {
		const execOpt = skCtx.execOptions;
		if (!execOpt.resetAll && (execOpt.autoComplete || execOpt.genAnnots)) planContinuousCheck(skCtx, node);
	}
}

SKMETALIB.registerMetaNode(new SkMSpTeSegment('SpTeSegment'));


//************** Spatial **************
// Exemple :
// <sp:zone>
//  <dk:zoneM>...</dk:zoneM>
//  <sc:spatial>
//   <sc:shape>...</sc:shape>
//   <sc:coords>...</sc:coords>
//  </sc:spatial>
//  <dk:comment>...</dk:comment>
// </sp:zone>
//
// soit
// <sng:oneOrMore>
// 	<sng:element name="sp:zone" sk:title="Zone" sk:model="SpTeSegment">
// 		<sng:ref name="dk_zoneM"/>
// 		<sng:element name="sc:spatial" sk:model="SpTeSpatial">
// 			<sng:element name="sc:shape" sk:model="SpTeSpatialShape" sk:shapes="rect circle poly">
// 				<sng:choice>
// 					<sng:value>rect</sng:value>
// 					<sng:value>circle</sng:value>
// 					<sng:value>poly</sng:value>
// 				</sng:choice>
// 			</sng:element>
// 			<sng:element name="sc:coords" sk:model="SpTeSpatialCoords" sk:shapes="rect circle poly">
// 				<sng:data type="string">
// 					<sng:param name="minLength">1</sng:param>
// 				</sng:data>
// 			</sng:element>
// 		</sng:element>
// 		<sng:ref name="dk_comment"/>
// 	</sng:element>
// </sng:oneOrMore>

/** SkMeta d'un élément modélisant la racine des propriétés spaciales d'un segment SpTe. */
class SkMSpTeSpatial extends SkMBase {

	get gramLevelType(): EGramLevelType {return EGramLevelType.metas}

	// onExecRules(skCtx: SkContext, rule: ISkRuleObj, nodeOrAttr: Node | Attr): void {
	// }
}

SKMETALIB.registerMetaNode(new SkMSpTeSpatial('SpTeSpatial'));

/** SkMeta d'un élément textuel modélisant la forme spaciale d'un segment SpTe. */
class SkMSpTeSpatialShape extends SkMSpTeSpatial {

	initSkRule(skRule: SkRuleNode & ISpTeSpatialShapeProps, confRule: Element): void {
		const v = confRule.getAttributeNS(SK_NS, 'shapes');
		skRule.shapes = (v ? v.split(' ') : ['rect']) as ISpTeShape[];
		skRule.coordsEltName = confRule.getAttributeNS(SK_NS, 'coordsEltName') || "sc:coords";
	}

	getCoordsElt(skRule: SkRuleNode & ISpTeSpatialShapeProps, shape: Element): Element {
		return DOM.findFirstChild(shape.parentNode, (n): n is Element => n.nodeName === skRule.coordsEltName);
	}

	onExecRules(skCtx: SkContext, rule: SkRuleNode & ISpTeSpatialShapeProps, node: Element): void {
		const execOpt = skCtx.execOptions;
		if (!execOpt.resetAll && (execOpt.autoComplete || execOpt.genAnnots)) {
			if (execOpt.pendingRevalid.has(node)) {
				//Cas particulier d'une revalidation partielle où seul le shape est modifié.
				const coordsElt = this.getCoordsElt(rule, node);
				if (coordsElt && !execOpt.pendingRevalid.has(coordsElt)) {
					(execOpt.shouldRevalid || (execOpt.shouldRevalid = new Set())).add(coordsElt);
				}
			}
		}
	}
}

interface ISpTeSpatialShapeProps {
	/** Formes autorisées. */
	shapes: ISpTeShape[]
	/** Nom de l'elt frère qui contient les coordonnées. */
	coordsEltName: string
}

SKMETALIB.registerMetaNode(new SkMSpTeSpatialShape('SpTeSpatialShape'));


/** SkMeta d'un élément textuel modélisant les coordonnées d'un segment d'un SpTe. */
class SkMSpTeSpatialCoords extends SkMSpTeSpatial {

	initSkRule(skRule: SkRuleNode & ISpTeSpatialCoordsProps, confRule: Element): void {
		const v = confRule.getAttributeNS(SK_NS, 'shapes');
		skRule.shapes = (v ? v.split(' ') : ['rect']) as ISpTeShape[];
		skRule.shapeEltName = confRule.getAttributeNS(SK_NS, 'shapeEltName') || "sc:shape";
	}

	getShapeElt(skRule: SkRuleNode & ISpTeSpatialCoordsProps, coordsElt: Element): Element | null {
		return DOM.findFirstChild(coordsElt.parentNode, (n): n is Element => n.nodeName === skRule.shapeEltName);
	}

	getShape(skRule: SkRuleNode & ISpTeSpatialCoordsProps, coordsElt: Element): ISpTeShape | null {
		const shapeElt = this.getShapeElt(skRule, coordsElt);
		const shape = shapeElt ? shapeElt.textContent : null;
		return skRule.shapes.indexOf(shape as any) >= 0 ? shape as any : null;
	}

	onExecRules(skCtx: SkContext, rule: SkRuleNode & ISpTeSpatialCoordsProps, node: Element): void {
		const execOpt = skCtx.execOptions;
		if (execOpt.autoComplete) {
			const shapeElt = this.getShapeElt(rule, node);
			if (shapeElt === null) return; //schéma invalide, va être corrigé, controle au prochain cycle.
			let shape = shapeElt.textContent as ISpTeShape | null;
			if (!shape || rule.shapes.indexOf(shape as any) < 0) {
				//On réimpose un shape valide.
				shape = rule.shapes[0];
				execOpt.corrections.push(replaceXmlContent(shapeElt, [shape]));
			}
			const coords = node.textContent;
			if (!coords || !this.isCoordsValid(shape, coords)) {
				const x = Math.floor(Math.random() * 100) + 200;
				const y = Math.floor(Math.random() * 100) + 200;
				let coords: string;
				if (shape === 'rect') {
					coords = `${x},${y},${x + 200},${y + 100}`;
				} else if (shape === 'circle') {
					coords = `${x},${y},100`;
				} else if (shape == 'ellipse') {
					coords = `${x},${y},100,50`;
				} else { //poly
					coords = `${x},${y},${x + 200},${y},${x + 100},${y + 100}`;
				}
				execOpt.corrections.push(replaceXmlContent(node, [coords]));
			}
		} else if (execOpt.genAnnots) {
			const shape = this.getShape(rule, node);
			if (!shape) {
				skCtx.addAnnot(new SkAnnotEltFree().init(node as Element, "La forme de cette zone est invalide"));
			} else if (!this.isCoordsValid(shape, node.textContent)) {
				skCtx.addAnnot(new SkAnnotEltFree().init(node as Element, "La définition de cette zone est invalide"));
			}
		}
	}

	private isCoordsValid(shape: ISpTeShape, coords: string) {
		//TODO
		return true;
	}
}

interface ISpTeSpatialCoordsProps {
	/** Formes autorisées. */
	shapes: ISpTeShape[]
	/** Nom de l'elt frère qui contient le shape. */
	shapeEltName: string
}

SKMETALIB.registerMetaNode(new SkMSpTeSpatialCoords('SpTeSpatialCoords'));


//************** Temporal **************
// Exemple :
// <sp:segment>
//  <x:xxxM>...</x:xxxM>
//  <sc:temporal>
//   <sc:start>68.175</sc:start>
//   <sc:end>138.462</sc:end>
//  </sc:temporal>
//  <x:seg>...</x:seg>
// </sp:segment>
//
// soit :
// 	<sng:element name="sp:segment" sk:title="Segment" sk:family="sub-level" sk:model="SpTeSegment">
// 			<sng:ref name="wm_optsPubWebM"/>
// 			<sng:element name="sc:temporal"  sk:model="SpTeContinuous" sk:track="">
// 					<sng:element name="sc:start"  sk:model="SpTeStart">
// 							<sng:data type="double"/>
// 					</sng:element>
// 					<sng:element name="sc:end"  sk:model="SpTeEnd">
// 							<sng:data type="double"/>
// 					</sng:element>
// 			</sng:element>
// 			<sng:ref name="wm_segment"/>
// 	</sng:element>


function planContinuousCheck(skCtx: SkContext, node: Element) {
	const schemaDom = skCtx.skNode.schemaDom;
	let parent = node;
	while (parent) {
		const compoRootNode = DOM.findParent(parent, null, (n): n is Element => {
			const skRule = schemaDom.getSkNode(n).rule;
			return skRule.skMeta instanceof SkMSpTeCompoRoot;
		});
		if (compoRootNode) {
			const execOpt = skCtx.execOptions;
			(execOpt.shouldRevalid || (execOpt.shouldRevalid = new Set())).add(compoRootNode);
		}
		parent = compoRootNode;
	}
}

/** SkMeta d'un élément modélisant la racine des propriétés temporelles d'un segment continue. */
export class SkMSpTeContinuous extends SkMBase {

	get gramLevelType(): EGramLevelType {return EGramLevelType.metas}

	initSkRule(skRule: SkRuleNode & ISpTeContinuousProps, confRule: Element): void {
		skRule.track = confRule.getAttributeNS(SK_NS, 'track') || "";
	}

	getTrack(skNode: SkNode): string {
		return (skNode.rule as SkRuleNode & ISpTeContinuousProps).track;
	}

	onExecRules(skCtx: SkContext, rule: SkRuleNode & ISpTeSpatialCoordsProps, node: Element): void {
		const execOpt = skCtx.execOptions;
		if (!execOpt.resetAll && (execOpt.autoComplete || execOpt.genAnnots)) planContinuousCheck(skCtx, node);
	}
}

interface ISpTeContinuousProps {
	/** Clé du track auquel appartient le segment. */
	track: string
}

SKMETALIB.registerMetaNode(new SkMSpTeContinuous('SpTeContinuous'));

/** SkMeta d'un élément textuel modélisant une borne du segment temporel. */
class SkMSpTeTime extends SkMBase {

	get gramLevelType(): EGramLevelType {return EGramLevelType.metas}

	onExecRules(skCtx: SkContext, rule: SkRuleNode & ISpTeSpatialCoordsProps, node: Element): void {
		const execOpt = skCtx.execOptions;
		if (execOpt.resetAll) {
			//Validation complète du doc.
			if (execOpt.importCtx && execOpt.autoMutate) {
				//Contenu qui va être importé, on passe les valeurs en négatif pour les marquer en "fuzzy"
				const v = node.textContent;
				if (v && v.charAt(0) !== '-') {
					const txtNode = DOM.findFirstChild(node, DOM.IS_text);
					if (DEBUG_SPTE) console.log("SkMSpTeTime fuzzy value : ", v, node.parentNode.parentNode.cloneNode(true));
					(execOpt.mutations || (execOpt.mutations = [])).push(new XmlInsertMsg().init(XA.append(XA.from(txtNode), 0), '-'));
				}
			}
			//la validation se fera plus haut.
			return;
		}
		if (execOpt.autoComplete || execOpt.genAnnots) {
			const schemaDom = skCtx.skNode.schemaDom;

			// 1. On cherche la track de ce time
			let track: string;
			let n = node.parentElement;
			do {
				const skNode = schemaDom.getSkNode(n);
				const skMeta = skNode.rule.skMeta;
				if (skMeta instanceof SkMSpTeContinuous) {
					track = skMeta.getTrack(skNode);
					break;
				}
				n = n.parentElement;
				if (!n) return;
			} while (true);

			// 2. On cherche la compo root gérant cette track.
			const compoRootNode = DOM.findParent(n.parentElement, null, (n): n is Element => {
				const skRule = schemaDom.getSkNode(n).rule;
				return skRule.skMeta instanceof SkMSpTeCompoRoot && skRule.skMeta.getContinuousTracks(skRule).indexOf(track) >= 0;
			});

			// 3. On va exiger la revalidation du compoRootNode...
			if (compoRootNode) {
				(execOpt.shouldRevalid || (execOpt.shouldRevalid = new Set())).add(compoRootNode);
			}
		}
	}
}

/** SkMeta d'un élément textuel modélisant la borne start du segment temporel. */
class SkMSpTeStart extends SkMSpTeTime {
}

SKMETALIB.registerMetaNode(new SkMSpTeStart('SpTeStart'));


/** SkMeta d'un élément textuel modélisant la borne end du segment temporel. */
class SkMSpTeEnd extends SkMSpTeTime {
}

SKMETALIB.registerMetaNode(new SkMSpTeEnd('SpTeEnd'));


/** Track temporel continu pour lequel un controle de continuité doit être réalisé. */
class SpTeRootContinuousTrack {

	constructor(public readonly track: string) {}

	onExecRules(skCtx: SkContext, rule: ISkRuleObj, node: Element) {
		const execOpts = skCtx.execOptions;
		//console.log("SpTeRootContinuousTrack:::::: '" + this.track + "'", node, execOpts);
		const seg = buildContinuousSeg(this.track, skCtx.skNode.schemaDom, node);
		if (!seg) return;
		const contMgr = (skCtx.skNode as ISpTeRootSkNode).continuousSegMgr;
		const streamEnd = contMgr ? contMgr._duration : undefined;
		const countNextSeg = seg._countNextSeg();
		minOptimalSegDur = streamEnd > 0 ? Math.min(1, round(streamEnd / countNextSeg)) : 1;
		try {
			if (seg.checkTree(0, streamEnd)) return; // contrôle rapide ok.
			//Problème
			if (execOpts.autoComplete) {
				// => recherche des modifications à réaliser
				if (DEBUG_SPTE) console.log("SpTeRootContinuousTrack fixFuzzyBoundaries:::", this.track, node.cloneNode(true));
				if (!seg.fixFuzzyBoundaries(execOpts.corrections, [seg], streamEnd)) {
					//Si aucun timecode flou fixé, on valide toute la chaine (sinon ce sera validé au prochain cycle)
					if (DEBUG_SPTE) console.log("SpTeRootContinuousTrack checkBoundaries:::", this.track, node.cloneNode(true));
					if (streamEnd > 0) seg.fixEndStream(execOpts.corrections, streamEnd);
					seg.checkBoundaries(execOpts.corrections, 0, countNextSeg, streamEnd, countNextSeg, streamEnd);
					if (streamEnd > 0) {
						const endReal = getEndLine(seg).endValue;
						if (endReal > streamEnd) {
							//Pb min technique > à la durée renseignée issue de la video, on abandonne cette durée de la vidéo.
							if (DEBUG_SPTE) console.log("Force duration from", contMgr._duration, "to", endReal);
							contMgr._duration = endReal;
						}
					}
				}
			} else if (execOpts.genAnnots) {
				// => Annotation générale
				skCtx.addAnnot(new SkAnnotEltFree().init(node, "La continuité temporelle des segments n'est pas correcte."));
			}
		} finally {
			minOptimalSegDur = 1;
		}
	}
}

class ContinuousSeg {

	next: ContinuousSeg | null;

	firstCh?: ContinuousSeg;
	lastCh?: ContinuousSeg;
	/** nb de segments contenus */
	countSeg: number = 1;

	startValue: number;
	startFuzzy: boolean;

	endValue: number;
	endFuzzy: boolean;

	/** Mémoire des offsets des msg de chgt de valeur dans le tableau des msgs en cas de réécriture (négo min seg / stream duration). */
	startMsgOffset?: number;
	endMsgOffset?: number;

	constructor(public startNode: Element, public endNode: Element, previous: ContinuousSeg) {
		this.startValue = parseFloat(startNode.textContent);
		if (Number.isNaN(this.startValue) || !Object.is(Math.abs(this.startValue), this.startValue)) {
			//Permet de détecter -0 comme fuzzy
			this.startValue = Math.abs(this.startValue);
			this.startFuzzy = true;
		}
		this.endValue = parseFloat(endNode.textContent);
		if (Number.isNaN(this.endValue) || !Object.is(Math.abs(this.endValue), this.endValue)) {
			//Permet de détecter -0 comme fuzzy
			this.endValue = Math.abs(this.endValue);
			this.endFuzzy = true;
		}
		if (previous) previous.next = this;
	}

	checkTree(start: number, endLine?: number): boolean {
		if (this.startValue !== start) return false;
		if (this.endValue - this.startValue > this._getMinDuration()) {
			if (this.firstCh && !this.firstCh.checkTree(start, this.endValue)) return false;
			if (this.next) {
				//check le suivant
				if (!this.next.checkTree(this.endValue, endLine)) return false;
			} else {
				//pas de suivant
				if (endLine !== undefined && this.endValue !== endLine) return false;
			}
			return true;
		}
		return false;
	}

	/** Impose la fin de la durée totale du stream. */
	fixEndStream(msgs: MsgOt[], streamEnd: number) {
		let seg: ContinuousSeg = this;
		let needFix = false;
		while (seg) {
			if (!seg.endValue || seg.endValue > streamEnd) {
				//La fin actuelle de ce segment est indéfini ou trop grand.
				needFix = true;
				if (seg.startValue >= streamEnd) seg.startFuzzy = true;
				if (!seg.next) seg.setEndRecurs(msgs, streamEnd); //c'est le dernier, on force.
				else seg.endFuzzy = true;
				seg.forceFuzzyChildren();
			} else {
				//Si dernier segment et valeur plus grande, on update que les bornes de fin.
				if (!seg.next && seg.endValue < streamEnd) seg.setEndRecurs(msgs, streamEnd);
			}
			seg = seg.next;
		}
		if (needFix) this.fixFuzzyBoundaries(msgs, [this], streamEnd);
	}

	protected forceFuzzyChildren() {
		let ch = this.firstCh;
		if (!ch) return;
		while (ch) {
			ch.startFuzzy = ch === this.firstCh ? this.startFuzzy : true;
			ch.endFuzzy = ch === this.lastCh ? this.endFuzzy : true;
			ch.forceFuzzyChildren();
			ch = ch.next;
		}
	}

	/**
	 * Corrige chaque segment.
	 * @param lineCountNextSeg Nb de segments finaux à partir de this dans la ligne de this.
	 * @param streamCountNextSeg Nb total de segments finaux à partir de this (donc y compris les descendants de this), jusqu'à la fin.
	 */
	checkBoundaries(msgs: MsgOt[], start: number, lineCountNextSeg: number, parentEnd: number | undefined, streamCountNextSeg: number, streamEnd: number | undefined) {
		//On impose le start sur la chaine des firstCh
		let seg: ContinuousSeg = this;
		const stackCh: ContinuousSeg[] = [];
		while (seg) {
			stackCh.push(seg);
			seg.setStart(msgs, start);
			seg = seg.firstCh;
		}
		//On parcourt les timeLines en remontant
		for (let i = stackCh.length - 1; i >= 0; i--) {
			seg = stackCh[i];
			//On affecte le endValue
			seg.setEnd(msgs, seg._computeEnd(lineCountNextSeg, parentEnd, streamCountNextSeg, streamEnd));
			//On parcourt cette timeLine.
			const next = seg.next;
			if (next) {
				const lineParentEnd = i === 0 ? parentEnd : stackCh[i - 1].endValue;
				next.checkBoundaries(msgs, seg.endValue, lineCountNextSeg - seg.countSeg, lineParentEnd, streamCountNextSeg - seg.countSeg, streamEnd);
			}
		}
	}

	_computeEnd(lineCountNextSeg: number, parentEnd: number | undefined, streamCountNextSeg: number, streamEnd: number | undefined) {
		if (this.lastCh) return this.lastCh.endValue; // timeline inférieure déjà calculée
		let end = this.endValue;
		if (!end || end - this.startValue < minOptimalSegDur/* * this.countSeg =1 car pas de fils*/) {
			//Aucune valeur renseignée, ou durée trop faible.
			//On cherche le 1er frère suivant valide et on divise la durée par le nb de frères.
			//Sinon on prend le endValue du segment parent
			let firstNextValid = this.next;
			let count = 1;/* this.countSeg =1 car pas de fils*/
			while (firstNextValid && !firstNextValid.startValue) {
				count += firstNextValid.countSeg;
				firstNextValid = firstNextValid.next;
			}
			if (firstNextValid) {
				//frère suivant valide trouvé
				end = round(this.startValue + Math.max((firstNextValid.startValue - this.startValue) / count /* * this.countSeg =1 car pas de fils*/, minOptimalSegDur /* * this.countSeg =1 car pas de fils*/));
			} else if (parentEnd) {
				//on s'appuie sur le parent
				end = round(this.startValue + Math.max((parentEnd - this.startValue) / lineCountNextSeg /* * this.countSeg =1 car pas de fils*/, minOptimalSegDur /* * this.countSeg =1 car pas de fils*/));
			} else {
				//val par défaut
				end = minOptimalSegDur /* * this.countSeg =1 car pas de fils*/;
			}
		}
		if (streamEnd) {
			//durée de la vidéo dispo,
			// on s'assure que le temps restant est compatible avec le nb de segments restants
			if (streamEnd - end < (streamCountNextSeg - 1 /* this.countSeg =1 car pas de fils*/) * minOptimalSegDur) {
				//Impossible, on va réduire en répartissant le temps total restant avec le nb total de segments, avec minSegDur en min absolu
				end = round(this.startValue + Math.max((streamEnd - this.startValue) / streamCountNextSeg /* * this.countSeg =1 car pas de fils*/, minSegDur /* * this.countSeg =1 car pas de fils*/));
			}
		}
		return end;
	}

	addSubTimeLine(subTimeLine: ContinuousSeg) {
		this.firstCh = subTimeLine;
		this.countSeg = subTimeLine.countSeg;
		let last = subTimeLine;
		while (last.next) {
			last = last.next;
			this.countSeg += last.countSeg;
		}
		this.lastCh = last;
	}

	/**
	 * @param ancestors Le 1er élément du tableau est le 1er seg racine, les suivants sont les ancêtres logiques des segments.
	 * @return true si des fixations de points fuzzy ont eu lieu.
	 */
	fixFuzzyBoundaries(msgs: MsgOt[], ancestors: ContinuousSeg[], streamEnd: number): boolean {
		if (this.startFuzzy || this.endFuzzy) {
			this._fixBoundariesFromNeighbours(msgs, ancestors, streamEnd);
			return true;
		} else {
			let fixed = false;
			if (this.next && this.next.fixFuzzyBoundaries(msgs, ancestors, streamEnd)) fixed = true;
			if (this.firstCh) {
				ancestors.push(this);
				if (this.firstCh.fixFuzzyBoundaries(msgs, ancestors, streamEnd)) fixed = true;
				ancestors.length--;
			}
			return fixed;
		}
	}

	/**
	 * @param ancestors Le 1er élément du tableau est le 1er seg racine, les suivants sont les ancêtres logiques des segments.
	 */
	_fixBoundariesFromNeighbours(msgs: MsgOt[], ancestors: ContinuousSeg[], streamEnd: number) {
		const prev = this._getPrevious(getStartLine(ancestors));
		let start = prev ? prev.endValue : ancestors.length > 1 ? ancestors[ancestors.length - 1].startValue : 0;
		const segToFix: ContinuousSeg[] = [this];
		const minDur = this._getMinDuration();
		let minDurAll = minDur;
		let curr = this as ContinuousSeg;
		let next = this.next;
		while (next && next.startFuzzy) {
			segToFix.push(next);
			minDurAll += next._getMinDuration();
			curr = next;
			next = next.next;
		}
		let end = limitEnd(next ?
			next.startValue : //next on fuzzy c'est l marque de fin.
			ancestors.length > 1 ?
				ancestors[ancestors.length - 1].endValue : //tous les next sont fuzzy, on prend le end du parent
				streamEnd !== undefined ? streamEnd : start + minDur,/* on est un seg racine terminal, on ne déplace pas la fin (respect de la durée du stream). */
			streamEnd);
		let preserveValues = false;
		let ancToMove = this.isFuzzyLogicMoveBeforeUp(segToFix, ancestors);
		if (ancToMove) {
			ancToMove.setEndRecurs(msgs, segToFix[segToFix.length - 1].endValue);
			preserveValues = true;
		} else if (ancToMove = this.isFuzzyLogicMoveBeforeRoot(segToFix, ancestors)) {
			ancToMove.setStartRecurs(msgs, segToFix[segToFix.length - 1].endValue);
			preserveValues = true;
		} else if (ancToMove = this.isFuzzyLogicMoveAfterUp(segToFix, ancestors)) {
			ancToMove.setStartRecurs(msgs, segToFix[0].startValue);
			preserveValues = true;
		} else if (ancToMove = this.isFuzzyLogicMoveAfterRoot(segToFix, ancestors)) {
			ancToMove.setEndRecurs(msgs, segToFix[0].startValue);
			preserveValues = true;
		}

		function fixEnd(seg: ContinuousSeg, segEnd: number) {
			segEnd = limitEnd(segEnd, streamEnd);
			seg.setEnd(msgs, segEnd);
			if (seg.next) seg.next.setStart(msgs, segEnd);
			if (seg.firstCh) {
				ancestors.push(seg);
				seg.firstCh._fixBoundariesFromNeighbours(msgs, ancestors, streamEnd);
				ancestors.length--;
			}
		}

		if (preserveValues) {
			//Les valeurs par défaut fuzzy sont OK avec celles des voisins fixés. On les présereve.
			this.setStart(msgs, start);
			for (const seg of segToFix) fixEnd(seg, seg.endValue);
		} else {
			//Les valeurs par défaut ne correspondent pas. On refragmente de façon équidistante avec respect des min.
			if (end < start + minDur) {
				//Nécessité de repousser davantage le end qui était fixé.
				if (next) {
					//cas simple, on a un suivant, on va répartir l'espace offert par le plus petit suivant
					let smallestNext = next;
					while (smallestNext.firstCh) smallestNext = smallestNext.firstCh;
					const delta = (smallestNext.endValue - smallestNext.startValue - minDurAll) / (segToFix.length + 1);
					if (delta > 0) {
						end = round(end + delta * segToFix.length);
						this.setStart(msgs, start);
					}
				} else if (prev) {
					//pas de suivant, on va tenter de prendre de la durée au plut petit précédent
					let smallestPrev = prev;
					while (smallestPrev.lastCh) smallestPrev = smallestPrev.lastCh;
					const delta = (smallestPrev.endValue - smallestPrev.startValue - minDurAll) / (segToFix.length + 1);
					if (delta > 0) {
						start = round(start - delta * segToFix.length);
						prev.setEndRecurs(msgs, start);
					}
				} else {
					//XXX on mange de la durée dans les ancêtres ?
					this.setStart(msgs, start);
				}
			} else {
				this.setStart(msgs, start);
			}
			const durToDistrib = Math.max(0, (end - start - minDurAll) / segToFix.length);
			for (const seg of segToFix) fixEnd(seg, round(seg.startValue + seg._getMinDuration() + durToDistrib));
		}
	}

	/**
	 * Evaluation du cas particulier d'une suite de segments qui ont été simplement déplacés avant dans l'arbre logique, sans changement
	 * des segments terminaux.
	 * Ce sont alors la fin des ancêtres qu'il faudra déplacer.
	 * @return le ContinuousSeg racine dont il faut modifier le end ou null
	 */
	protected isFuzzyLogicMoveBeforeUp(segToFix: ContinuousSeg[], ancestors: ContinuousSeg[]): ContinuousSeg {
		let anc = ancestors[ancestors.length - 1];
		if (!anc) return null;
		const firstSeg = segToFix[0];
		const lastSeg = segToFix[segToFix.length - 1];
		if (!lastSeg.next && firstSeg.startValue === anc.endValue) {
			//On est dans ce contexte de déplacement logique : on cherche l'ancêtre racine qui a le même endValue;
			let i = ancestors.length - 2;
			while (i >= 1 && !anc.next) anc = ancestors[i--];
			return anc;
		}
		return null;
	}

	/**
	 * Evaluation du cas particulier d'une suite de segments qui ont été simplement remontés avant, à la racine de l'arbre logique, sans changement
	 * des segments terminaux.
	 * C'est alors le début du segment racine suivant qu'il faut déplacer.
	 * @return le ContinuousSeg racine dont il faut modifier le start ou null
	 */
	protected isFuzzyLogicMoveBeforeRoot(segToFix: ContinuousSeg[], ancestors: ContinuousSeg[]): ContinuousSeg {
		if (ancestors.length > 1) return null;
		const firstSeg = segToFix[0];
		const lastSeg = segToFix[segToFix.length - 1];
		if (lastSeg.next && firstSeg.startValue === lastSeg.next.startValue) return lastSeg.next;
		return null;
	}


	/**
	 * Evaluation du cas particulier d'une suite de segments qui ont été simplement déplacés après dans l'arbre logique, sans changement
	 * des segments terminaux.
	 * Ce sont alors le début des ancêtres qu'il faudra déplacer.
	 * @return le ContinuousSeg racine dont il faut modifier le début ou null
	 */
	protected isFuzzyLogicMoveAfterUp(segToFix: ContinuousSeg[], ancestors: ContinuousSeg[]): ContinuousSeg {
		let anc = ancestors[ancestors.length - 1];
		if (!anc) return null;
		const firstSeg = segToFix[0];
		const lastSeg = segToFix[segToFix.length - 1];
		if (!firstSeg._getPrevious(getStartLine(ancestors)) && lastSeg.endValue === anc.startValue) {
			//On est dans ce contexte de déplacement logique : on cherche l'ancêtre racine qui a le même endValue;
			for (let i = ancestors.length - 2; i >= 1 && ancestors[i].startValue === lastSeg.endValue; i--) anc = ancestors[i];
			return anc;
		}
		return null;
	}

	/**
	 * Evaluation du cas particulier d'une suite de segments qui ont été simplement remontés après, à la racine de l'arbre logique, sans changement
	 * des segments terminaux.
	 * C'est alors la fin du segment racine précédent qu'il faut déplacer.
	 * @return le ContinuousSeg racine dont il faut modifier le end ou null
	 */
	protected isFuzzyLogicMoveAfterRoot(segToFix: ContinuousSeg[], ancestors: ContinuousSeg[]): ContinuousSeg {
		if (ancestors.length > 1) return null;
		const firstSeg = segToFix[0];
		const lastSeg = segToFix[segToFix.length - 1];
		const prev = firstSeg._getPrevious(getStartLine(ancestors));
		if (prev && lastSeg.endValue === prev.startValue) return prev;
		return null;
	}

	setStart(msgs: MsgOt[], start: number, startStr?: string) {
		if (this.startValue === start && !this.startFuzzy) return;
		if (this.startMsgOffset === undefined) this.startMsgOffset = msgs.length;
		msgs[this.startMsgOffset] = replaceXmlContent(this.startNode, [startStr || start.toFixed(3)]);
		if (DEBUG_SPTE) console.log("SET start for segment: ", XA.from(this.startNode.parentElement.parentElement), this.startValue, '->', start, startStr);
		this.startValue = start;
		if (this.startFuzzy) this.startFuzzy = false;
	}

	setStartRecurs(msgs: MsgOt[], start: number) {
		const str = start.toFixed(3);
		let seg = this as ContinuousSeg;
		while (seg) {
			seg.setStart(msgs, start, str);
			seg = seg.firstCh;
		}
	}

	setEnd(msgs: MsgOt[], end: number, endStr?: string) {
		if (this.endValue === end && !this.endFuzzy) return;
		if (this.endMsgOffset === undefined) this.endMsgOffset = msgs.length;
		msgs[this.endMsgOffset] = replaceXmlContent(this.endNode, [endStr || end.toFixed(3)]);
		if (DEBUG_SPTE) console.log("SET end for segment: ", XA.from(this.endNode.parentElement.parentElement), this.endValue, '->', end, endStr);
		this.endValue = end;
		if (this.endFuzzy) this.endFuzzy = false;
	}

	setEndRecurs(msgs: MsgOt[], end: number) {
		const str = end.toFixed(3);
		let seg = this as ContinuousSeg;
		while (seg) {
			seg.setEnd(msgs, end, str);
			if (seg.next) {
				seg.next.setStart(msgs, end, str);
				let nextCh = seg.next.firstCh;
				while (nextCh) {
					nextCh.setStart(msgs, end, str);
					nextCh = nextCh.firstCh;
				}
			}
			seg = seg.lastCh;
		}
	}

	_getPrevious(startLine: ContinuousSeg): ContinuousSeg {
		if (startLine === this) return null;
		let p = startLine;
		while (p && p.next !== this) p = p.next;
		return p;
	}

	_getMinDuration() {
		return minOptimalSegDur * this.countSeg;
	}

	_countNextSeg(): number {
		let c = 0;
		for (let s: ContinuousSeg = this; s; s = s.next) c += s.countSeg;
		return c;
	}

	forceEnd(temporalElt: Element, value: number, ancestors: ContinuousSeg[], msgs: MsgOt[], forInsertAfter: boolean): boolean {
		if (this.endNode.parentNode === temporalElt) {
			//Trouvé
			if (forInsertAfter) {
				this.setEnd(msgs, value);
				if (this.lastCh) this.lastCh.setEndRecurs(msgs, value);
			} else {
				//on remonte à la racine de ce end
				let anc = this as ContinuousSeg;
				let i = ancestors.length - 1;
				while (!anc.next && i >= 1) anc = ancestors[i--];
				anc.setEndRecurs(msgs, value);
			}
			return true;
		}
		if (this.next && this.next.forceEnd(temporalElt, value, ancestors, msgs, forInsertAfter)) return true;
		if (this.firstCh) {
			ancestors.push(this);
			if (this.firstCh.forceEnd(temporalElt, value, ancestors, msgs, forInsertAfter)) return true;
			ancestors.length--;
		}
		return false;
	}

	forceStart(temporalElt: Element, value: number, ancestors: ContinuousSeg[], msgs: MsgOt[], forInsertBefore: boolean): boolean {
		if (this.startNode.parentNode === temporalElt) {
			//Trouvé
			if (forInsertBefore) {
				this.setStartRecurs(msgs, value);
			} else {
				//On cherche le previous en remontant pour lui forcer son end
				let prev = this._getPrevious(getStartLine(ancestors));
				if (!prev) {
					ancestors = ancestors.concat();
					while (!prev && ancestors.length > 1) {
						const anc = ancestors[ancestors.length - 1];
						ancestors.length--;
						prev = anc._getPrevious(getStartLine(ancestors));
					}
				}
				return prev ? prev.forceEnd(temporalElt, value, ancestors, msgs, false) : false;
			}
		}
		if (this.next && this.next.forceStart(temporalElt, value, ancestors, msgs, forInsertBefore)) return true;
		if (this.firstCh) {
			ancestors.push(this);
			if (this.firstCh.forceStart(temporalElt, value, ancestors, msgs, forInsertBefore)) return true;
			ancestors.length--;
		}
		return false;
	}
}

function limitEnd(end: number, endStream: number | undefined) {
	return Math.max(0, endStream == undefined ? end : Math.min(end, endStream));
}

function buildContinuousSeg(track: string, schemaDom: SchemaDom, compo: Element): ContinuousSeg {
	let firstSeg: ContinuousSeg;
	let seg: ContinuousSeg;
	for (let ch = compo.firstElementChild; ch; ch = ch.nextElementSibling) {
		const skNode = schemaDom.getSkNode(ch);
		const skMeta = skNode.rule.skMeta;
		if (skMeta instanceof SkMSpTeSegment) {
			//Segment trouvé
			//On cherche une définition d'un segment temporel associé à cette track.
			const eltCont = DOM.findFirstChild(ch, (n: Node): n is Element => {
				if (!IS_element(n)) return false;
				const skNodeCont = schemaDom.getSkNode(n);
				const skMetaCont = skNodeCont.rule.skMeta;
				return skMetaCont instanceof SkMSpTeContinuous && skMetaCont.getTrack(skNodeCont) === track;
			});
			if (eltCont) {
				const eltStart = DOM.findFirstChild(eltCont, (n: Node): n is Element => {
					if (!IS_element(n)) return false;
					return schemaDom.getSkNode(n).rule.skMeta instanceof SkMSpTeStart;
				});
				const eltEnd = DOM.findFirstChild(eltCont, (n: Node): n is Element => {
					if (!IS_element(n)) return false;
					return schemaDom.getSkNode(n).rule.skMeta instanceof SkMSpTeEnd;
				});
				if (eltStart && eltEnd) {
					seg = new ContinuousSeg(eltStart, eltEnd, seg);
					if (!firstSeg) firstSeg = seg;
					const eltSubCompo = DOM.findFirstChild(ch, (n: Node): n is Element => {
						if (!IS_element(n)) return false;
						const rule = schemaDom.getSkNode(n).rule;
						if (!(rule.skMeta instanceof SkMSpTeCompo)) return false;
						if (rule.skMeta instanceof SkMSpTeCompoRoot) {
							//Si une compoRoot traite la même track, on stop l'arbre ici (cas de récursivité).
							if (rule.skMeta.getContinuousTracks(rule).indexOf(track) >= 0) return false;
						}
						return true;
					});
					if (eltSubCompo) {
						const subTimeLine = buildContinuousSeg(track, schemaDom, eltSubCompo);
						if (subTimeLine) seg.addSubTimeLine(subTimeLine);
					}
				}
			}
		}
	}
	return firstSeg;
}

/**
 * Retourne le 1er segment de la ligne courante.
 *
 * @param ancestors Le 1er élément du tableau est le 1er seg racine, les suivants sont les ancêtres logiques des segments.
 */
function getStartLine(ancestors: ContinuousSeg[], offset: number = ancestors.length - 1): ContinuousSeg {
	return offset === 0 ? ancestors[0] : ancestors[offset].firstCh;
}

function getEndLine(from: ContinuousSeg): ContinuousSeg {
	while (from.next) from = from.next;
	return from;
}

function round(v: number): number {
	return Math.round(v * 1000) / 1000;
}

class SetDurationMsg extends XmlAddrMsg implements IMsgUpdater {
	static readonly type = "setDur";
	oldDur: number;
	newDur: number;

	get isBodyMutator(): boolean {return true}

	init(start: IXAddr, end?: IXAddr): this {
		throw Error("do not use");
	}

	initDur(spTeCompoRoot: IXAddr, oldDur: number, newDur: number): this {
		this.oldDur = oldDur;
		this.newDur = newDur;
		this.setMeta("autoCorrect", true);
		return super.init(spTeCompoRoot);
	}

	invert(): SetDurationMsg {
		return new SetDurationMsg().initDur(this.addr, this.newDur, this.oldDur);
	}

	applyUpdates(state: State): void {
		if (this.addr && state instanceof XmlTypedState) {
			const schemaDom = state.schemaDom;
			const spTeRootNode = XA.findDomLast(this.addr, schemaDom.nodeRoot);
			const skNode = schemaDom.getSkNode(spTeRootNode);
			const skMeta = skNode.rule.skMeta;
			if (skMeta.id === "SpTeCompoRoot") {
				const mgr = skMeta.getAspect<ContinuousSegMgr>("ContinuousSegMgr", skNode.rule, skNode, {track: ""});
				//console.log("mgr:::::", mgr);
				mgr._duration = this.newDur;
			}
		}
	}
}

House.MSG_FACTORYREG.register(SetDurationMsg);

/**
 * Aspect public pour modifier un arbre de segments continus.
 */
export class ContinuousSegMgr {

	_duration: number | undefined;

	constructor(readonly skNode: SkNode, readonly track: string) {
	}

	forceStart(xaTemporalSeg: IXAddr, time: number, forInsertBefore?: boolean): MsgOt[] {
		const temporalElt = XA.findDomLast(xaTemporalSeg, this.skNode.node.ownerDocument);
		if (!DOM.IS_element(temporalElt)) return null;
		const seg = buildContinuousSeg(this.track, this.skNode.schemaDom, this.skNode.node as Element);
		const result: MsgOt[] = [];
		seg.forceStart(temporalElt, time, [seg], result, forInsertBefore);
		if (DEBUG_SPTE) console.log("ContinuousSegMgr forceStart:: ", this._duration, result);
		return result;
	}

	forceEnd(xaTemporalSeg: IXAddr, time: number, forInsertAfter?: boolean): MsgOt[] {
		const temporalElt = XA.findDomLast(xaTemporalSeg, this.skNode.node.ownerDocument);
		if (!DOM.IS_element(temporalElt)) return null;
		const seg = buildContinuousSeg(this.track, this.skNode.schemaDom, this.skNode.node as Element);
		const result: MsgOt[] = [];
		seg.forceEnd(temporalElt, time, [seg], result, forInsertAfter);
		if (DEBUG_SPTE) console.log("ContinuousSegMgr forceEnd:: ", this._duration, result);
		return result;
	}

	needFixDuration(durInS: number): boolean {
		if (this._duration !== round(durInS)) return true;
		if (this._duration > 0) {
			const seg = buildContinuousSeg(this.track, this.skNode.schemaDom, this.skNode.node as Element);
			if (seg) return !seg.checkTree(0, this._duration);
		}
		return false;
	}

	setDuration(durInS: number | undefined, tolerance?: number): MsgOt[] | null {
		if (durInS > minSegDur) {
			durInS = round(durInS);
			const old = this._duration;
			const seg = buildContinuousSeg(this.track, this.skNode.schemaDom, this.skNode.node as Element);
			if (!seg) {
				this._duration = durInS;
				return null;
			}
			if (tolerance > 0 && this._duration > 0 && Math.abs(this._duration - durInS) <= tolerance) durInS = this._duration; //approximation, on garde la précédente durée
			const minTech = seg._countNextSeg() * minSegDur;
			if (durInS < minTech) durInS = minTech; //la durée en ms ne peut être inférieure au nb de segments terminaux : 1ms par segment.
			this._duration = durInS;
			const msgs: MsgOt[] = [];
			seg.fixEndStream(msgs, this._duration);
			if (msgs.length === 0) return null;
			if (DEBUG_SPTE) console.log("ContinuousSegMgr setDuration:: ", this._duration, msgs);
			msgs.push(new SetDurationMsg().initDur(XA.from(this.skNode.node), old, this._duration));
			return msgs;
		} else {
			this._duration = undefined;
			return null;
		}
	}
}

export const minSegDur = .001;
export let minOptimalSegDur = 1;

const DEBUG_SPTE = false;